import { useState, type ReactNode } from "react";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { TermSheetExtraSection, TermSheetSectionRow } from "./types";
import { TermSheetCustomSectionEditor } from "./TermSheetCustomSectionEditor";

const BRAND = "#0E4664";

interface TermSheetSectionListProps {
  sections: TermSheetSectionRow[];
  extraSections: TermSheetExtraSection[];
  rowOrder: string[];
  onRowOrderChange: (order: string[]) => void;
  expandedSectionId: string | false;
  onExpandedSectionIdChange: (id: string | false) => void;
  onUpdateSectionContent: (sectionId: string, content: string) => void;
  onToggleSectionIncluded: (sectionId: string, included: boolean) => void;
  onAddExtraSection: (afterSectionId: string) => void;
  onUpdateExtraSection: (section: TermSheetExtraSection) => void;
  onDeleteExtraSection: (sectionId: string) => void;
}

interface SortableRowProps {
  id: string;
  children: ReactNode;
}

function SortableRow({ id, children }: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    // Translate only — Transform also applies scale, which stretches variable-height rows.
    transform: CSS.Translate.toString(transform),
    transition: isDragging ? undefined : transition,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 1.5,
        opacity: isDragging ? 0.35 : 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
        <IconButton
          ref={setActivatorNodeRef}
          size="small"
          aria-label="Drag to reorder section"
          sx={{
            mt: 0.75,
            color: "#9CA3AF",
            cursor: isDragging ? "grabbing" : "grab",
            flexShrink: 0,
            touchAction: "none",
            "&:hover": { color: BRAND },
          }}
          {...attributes}
          {...listeners}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
      </Box>
    </Box>
  );
}

function DragOverlayRow({ label, isCustom }: { label: string; isCustom?: boolean }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 0.5,
        px: 0.5,
      }}
    >
      <IconButton
        size="small"
        disabled
        sx={{
          mt: 0.75,
          color: "#9CA3AF",
          flexShrink: 0,
          cursor: "grabbing",
        }}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          borderRadius: "10px",
          border: "1px solid #E8E5E0",
          bgcolor: "#FFFFFF",
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
          px: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <Box
            component="span"
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: BRAND,
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontWeight: 600,
              color: "#182737",
              lineHeight: 1.25,
            }}
          >
            {isCustom ? label.trim() || "Custom section" : label}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export function TermSheetSectionList({
  sections,
  extraSections,
  rowOrder,
  onRowOrderChange,
  expandedSectionId,
  onExpandedSectionIdChange,
  onUpdateSectionContent,
  onToggleSectionIncluded,
  onAddExtraSection,
  onUpdateExtraSection,
  onDeleteExtraSection,
}: TermSheetSectionListProps) {
  const [activeRowId, setActiveRowId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveRowId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = rowOrder.indexOf(String(active.id));
    const newIndex = rowOrder.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    onRowOrderChange(arrayMove(rowOrder, oldIndex, newIndex));
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveRowId(String(event.active.id));
  };

  const handleDragCancel = () => {
    setActiveRowId(null);
  };

  const getRowOverlayLabel = (rowId: string) => {
    const builtin = sections.find((section) => section.id === rowId);
    if (builtin) {
      return { label: builtin.label, isCustom: false };
    }

    const custom = extraSections.find((section) => section.id === rowId);
    if (custom) {
      return { label: custom.label, isCustom: true };
    }

    return { label: "Section", isCustom: false };
  };

  const activeOverlay = activeRowId ? getRowOverlayLabel(activeRowId) : null;

  const renderBuiltinSection = (section: TermSheetSectionRow) => (
    <>
      <Accordion
        expanded={expandedSectionId === section.id}
        onChange={(_event, isExpanded) =>
          onExpandedSectionIdChange(isExpanded ? section.id : false)
        }
        sx={{
          borderRadius: "10px !important",
          border: "1px solid #E8E5E0",
          bgcolor: "#FFFFFF",
          boxShadow: "none",
          "&:before": { display: "none" },
          "&.Mui-expanded": { mb: 0 },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "#9CA3AF" }} />}
          sx={{
            px: 2,
            minHeight: 48,
            "& .MuiAccordionSummary-content": {
              alignItems: "center",
              my: 1.25,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              minWidth: 0,
            }}
          >
            <Box
              component="span"
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: section.included ? BRAND : "#C5C0B8",
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontWeight: 600,
                color: "#182737",
                lineHeight: 1.25,
              }}
            >
              {section.label}
            </Typography>
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography sx={{ fontSize: "0.875rem", color: "#374151" }}>
              Include in preview
            </Typography>
            <Switch
              checked={section.included}
              onChange={(event) =>
                onToggleSectionIncluded(section.id, event.target.checked)
              }
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": { color: BRAND },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  bgcolor: BRAND,
                },
              }}
            />
          </Box>

          <Typography
            sx={{
              fontSize: "0.6875rem",
              fontWeight: 600,
              letterSpacing: "0.08em",
              color: "#9CA3AF",
              mb: 1,
            }}
          >
            SECTION TEXT
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            value={section.content}
            onChange={(event) =>
              onUpdateSectionContent(section.id, event.target.value)
            }
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "#FAFAF9",
                borderRadius: "8px",
                fontSize: "0.875rem",
                lineHeight: 1.5,
              },
            }}
          />
        </AccordionDetails>
      </Accordion>

      <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 1 }}>
        <IconButton
          size="small"
          onClick={() => onAddExtraSection(section.id)}
          aria-label={`Add section after ${section.label}`}
          sx={{
            color: BRAND,
            border: "1px solid #E0DDD8",
            borderRadius: "8px",
            bgcolor: "#FFFFFF",
            "&:hover": { bgcolor: "#F5F3F0" },
          }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>
    </>
  );

  const renderRow = (rowId: string) => {
    const builtin = sections.find((section) => section.id === rowId);
    if (builtin) {
      return renderBuiltinSection(builtin);
    }

    const custom = extraSections.find((section) => section.id === rowId);
    if (custom) {
      return (
        <TermSheetCustomSectionEditor
          section={custom}
          onChange={onUpdateExtraSection}
          onDelete={() => onDeleteExtraSection(custom.id)}
        />
      );
    }

    return null;
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={rowOrder} strategy={verticalListSortingStrategy}>
        {rowOrder.map((rowId) => (
          <SortableRow key={rowId} id={rowId}>
            {renderRow(rowId)}
          </SortableRow>
        ))}
      </SortableContext>

      <DragOverlay dropAnimation={null}>
        {activeOverlay ? (
          <DragOverlayRow
            label={activeOverlay.label}
            isCustom={activeOverlay.isCustom}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
